import React, { useContext } from 'react';
import './ContentLocked.scss';
import Button, { BUTTON_VARIANTS } from '../UI/Button/Button';
import { ModalContext } from '../ModalManager';
import CONTENT_RESTRICTION_RULE from '../../../const/contentRestrictionRule';
import createLink from "../../../libs/createLink";
import { useNavigate } from "react-router-dom";
import classNames from 'classnames';
import useLocalBreakpoint from '../../../hooks/useLocalBreakpoint';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHandsHoldingHeart } from '@fortawesome/pro-thin-svg-icons';
import { useDrawer } from '../DrawerProvider';
import OffersModal, { OFFERS_MODAL_CONTEXT, OFFERS_DRAWER_OPTIONS } from '../OffersModal/OffersModal';

// Cartes / bannières de verrouillage HORS présentation d'offre : gate d'inscription (invité),
// gates contributeur / réglages du propriétaire, verrous de modération, et bannières de quota.
// TOUTE présentation d'offre vit dans OffersModal — ici, un CTA d'offre ne fait qu'OUVRIR la
// modale centralisée (jamais de navigation directe vers /paiement, jamais de pitch maison).
// Les anciens gabarits premium/Plus (repost, story, messenger…) ont été supprimés : ces
// restrictions ouvrent OffersModal directement (cf. useRestriction).

const ACTION_LABELS = {
    SIGNUP: 'SIGNUP',
    SEE_OFFERS: 'SEE_OFFERS',
    CONTRIBUTE: 'CONTRIBUTE'
}

const BUTTON_TEXTS = {
    SIGNUP: "Créer un compte",
    SEE_OFFERS: 'Voir les offres',
    CANCEL: 'Peut-être plus tard',
    CONTRIBUTE: 'Compléter mon profil'
}

const BACKGROUNDS_LABELS = {
    YELLOW: 'YELLOW',
    BLUE: 'BLUE',
    PINK: 'PINK',
    PURPLE: 'PURPLE',
    GREEN: 'GREEN',
    DARK: 'DARK'
}

const BACKGROUNDS = {
    [BACKGROUNDS_LABELS.YELLOW]: require("./images/backgrounds/bg-yellow.png"),
    [BACKGROUNDS_LABELS.BLUE]: require("./images/backgrounds/bg-blue.png"),
    [BACKGROUNDS_LABELS.PINK]: require("./images/backgrounds/bg-pink.png"),
    [BACKGROUNDS_LABELS.PURPLE]: require("./images/backgrounds/bg-purple.png"),
    [BACKGROUNDS_LABELS.GREEN]: require("./images/backgrounds/bg-green.png"),
    [BACKGROUNDS_LABELS.DARK]: require("./images/backgrounds/bg-dark.png"),
}

const BACKGROUNDS_90 = {
    [BACKGROUNDS_LABELS.YELLOW]: require("./images/backgrounds/bg-yellow-90.png"),
    [BACKGROUNDS_LABELS.BLUE]: require("./images/backgrounds/bg-blue-90.png"),
    [BACKGROUNDS_LABELS.PINK]: require("./images/backgrounds/bg-pink-90.png"),
    [BACKGROUNDS_LABELS.PURPLE]: require("./images/backgrounds/bg-purple-90.png"),
    [BACKGROUNDS_LABELS.GREEN]: require("./images/backgrounds/bg-green-90.png"),
}

// Règle -> capability déclenchante de l'OffersModal : le CTA « Voir les offres » ouvre la
// modale ciblée sur la feature (comparatif générique si la règle n'est pas mappée).
const RULE_TO_OFFERS_TRIGGER = {
    [CONTENT_RESTRICTION_RULE.USER_BOOKMARKS_LIMIT]: 'canSaveUnlimitedBookmarks',
    [CONTENT_RESTRICTION_RULE.USER_NOTIFICATIONS_LIMIT]: 'canViewAllNotifications',
    [CONTENT_RESTRICTION_RULE.VIDEO_DURATION_LIMIT]: 'canWatchFullVideos',
};

export const CONTENT_LOCKED_TYPES = {
    // --- Bannières de quota (levés par canSaveUnlimitedBookmarks / canViewAllNotifications,
    // soit Voissa Plus OU certification) : le CTA ouvre la modale d'offres ciblée. ---
    [CONTENT_RESTRICTION_RULE.USER_BOOKMARKS_LIMIT]: {
        title: "Vous souhaitez garder tous vos favoris ?",
        description: "Seuls vos premiers favoris sont affichés. Passez à Voissa Plus pour les conserver tous, sans limite.",
        btnText: BUTTON_TEXTS.SEE_OFFERS,
        action: ACTION_LABELS.SEE_OFFERS,
        background: BACKGROUNDS_LABELS.PURPLE,
        image: require("./images/icons/bookmarks.png"),
    },
    [CONTENT_RESTRICTION_RULE.USER_NOTIFICATIONS_LIMIT]: {
        title: "Vous souhaitez voir toutes vos notifications ?",
        description: "Seules vos dernières notifications sont affichées. Passez à Voissa Plus pour toutes les retrouver.",
        btnText: BUTTON_TEXTS.SEE_OFFERS,
        action: ACTION_LABELS.SEE_OFFERS,
        background: BACKGROUNDS_LABELS.PURPLE,
        image: require("./images/icons/notifications.png"),
    },

    // --- Gate d'inscription : la limite vidéo pour un INVITÉ (un membre connecté passe par
    // l'OffersModal, cf. useRestriction VIDEO_DURATION_LIMIT). ---
    [CONTENT_RESTRICTION_RULE.VIDEO_DURATION_LIMIT]: ({ videoAgeRestrictionDays }) => {
        const getAgeText = (days) => {
            if (days === 2) return "48 heures";
            if (days === 7) return "7 jours";
            if (days === 30) return "30 jours";
            return `${days} jours`;
        };

        return {
            title: "Inscrivez-vous pour accéder à cette vidéo",
            description: `Cette vidéo a plus de ${getAgeText(videoAgeRestrictionDays)}, elle est limitée à 15 secondes pour les utilisateurs non inscrits`,
            btnText: BUTTON_TEXTS.SIGNUP,
            action: ACTION_LABELS.SIGNUP,
            background: BACKGROUNDS_LABELS.PINK,
            image: require("./images/icons/video.png"),
        };
    },

    // --- Gates contributeur / réglages du propriétaire (flags niveau contenu, pas des offres) ---
    [CONTENT_RESTRICTION_RULE.FOLLOW_MEMBER]: ({ username }) => ({
        title: <>Action réservée aux <b>contributeurs</b></>,
        description: <>
            Vous devez être <strong>contributeur</strong> pour suivre <strong>{username || 'ce membre'}</strong>.<br /><br />
            Pour devenir contributeur, complétez votre profil.
        </>,
        btnText: BUTTON_TEXTS.CONTRIBUTE,
        action: ACTION_LABELS.CONTRIBUTE,
        onlyIcon: <FontAwesomeIcon icon={faHandsHoldingHeart} />
    }),
    [CONTENT_RESTRICTION_RULE.BOOKMARK_MEDIA]: ({ contributorGate, ownerDisabledFavorites, username }) => {
        if (ownerDisabledFavorites) {
            // Le propriétaire a désactivé complètement les favoris : aucun CTA possible.
            return {
                title: <>Ajout en favoris <b>désactivé</b></>,
                description: <>
                    <strong>{username || 'Ce membre'}</strong> a désactivé l’ajout en favoris de ses publications.
                </>,
                onlyIcon: <FontAwesomeIcon icon={faHandsHoldingHeart} />
            };
        }
        if (contributorGate) {
            return {
                title: <>Ajout en favoris réservé aux <b>contributeurs</b></>,
                description: <>
                    <strong>{username || 'Ce membre'}</strong> réserve l’ajout en favoris aux <strong>contributeurs</strong>.<br /><br />
                    Pour devenir contributeur, complétez votre profil.
                </>,
                btnText: BUTTON_TEXTS.CONTRIBUTE,
                action: ACTION_LABELS.CONTRIBUTE,
                onlyIcon: <FontAwesomeIcon icon={faHandsHoldingHeart} />
            };
        }

        // Verrou CASL (cannot create Bookmark) = restriction de MODÉRATION (blocage de création),
        // pas une offre : message neutre, aucun pitch d'abonnement.
        return {
            title: <>Ajout en favoris <b>indisponible</b></>,
            description: <>Votre compte ne permet pas cette action pour le moment.</>,
            onlyIcon: <FontAwesomeIcon icon={faHandsHoldingHeart} />
        };
    },

    // --- Verrous de modération (CASL) : messages neutres, jamais de pitch d'offre ---
    // Sans cette entrée le drawer s'ouvrait VIDE (ContentLocked rendait null).
    [CONTENT_RESTRICTION_RULE.CREATE_PUBLICATION_MEDIA]: () => ({
        title: <>Publication <b>indisponible</b></>,
        description: <>Votre compte ne permet pas de publier pour le moment.</>,
        onlyIcon: <FontAwesomeIcon icon={faHandsHoldingHeart} />
    }),
    [CONTENT_RESTRICTION_RULE.MEDIA_CONTRIBUTOR]: ({ media }) => ({
        title: <>Publications réservées aux <b>contributeurs</b></>,
        description: <>
            Vous devez être <strong>contributeur</strong> pour voir cette publication.<br /><br />
            Pour devenir contributeur, complétez votre profil.
        </>,
        btnText: BUTTON_TEXTS.CONTRIBUTE,
        action: ACTION_LABELS.CONTRIBUTE,
        onlyIcon: <FontAwesomeIcon icon={faHandsHoldingHeart} />
    }),

}

const breakpoints = {
    md: 768
};

const ContentLocked = ({ restrictionRule, context = {}, inDrawer = false, handleCancel, ...props }) => {
    const { openModal } = useContext(ModalContext);
    const { openDrawer, closeDrawer } = useDrawer();
    const navigate = useNavigate();
    const [breakpoint, ref] = useLocalBreakpoint(breakpoints);

    let selectedContent = CONTENT_LOCKED_TYPES[restrictionRule];
    context = { ...context, restrictionRule };
    if (typeof selectedContent === 'function') selectedContent = selectedContent(context);

    if (!selectedContent) {
        return null;

    }

    const handleAction = async (action) => {
        // CTA d'offre : ouvre la modale d'offres centralisée, ciblée sur la capability de la règle.
        if (action === ACTION_LABELS.SEE_OFFERS) {
            openDrawer(
                <OffersModal
                    context={OFFERS_MODAL_CONTEXT.BLOCKED}
                    triggerAction={RULE_TO_OFFERS_TRIGGER[restrictionRule] || null}
                    inDrawer
                    handleCancel={closeDrawer}
                />,
                OFFERS_DRAWER_OPTIONS
            );
            return;
        }

        if (inDrawer) handleCancel();
        switch (action) {
            case ACTION_LABELS.SIGNUP:
                openModal('Signup', { context });
                break;
            case ACTION_LABELS.CONTRIBUTE:
                navigate(createLink("userMeSettingsContributorStatus"));
                break;
            default:
                return;
        }
    }

    const isBannerFormat = !breakpoint.md;

    let defaultBackground = BACKGROUNDS[selectedContent.background];
    if (isBannerFormat) defaultBackground = BACKGROUNDS_90[selectedContent.background];

    const backgroundStyle = {
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center bottom',
        backgroundImage: `url(${selectedContent.backgroundCustom || defaultBackground})`,

    }

    if (isBannerFormat) {
        backgroundStyle.backgroundSize = 'cover';
        backgroundStyle.backgroundPosition = 'center right';
        backgroundStyle.backgroundRepeat = 'no-repeat';
    }

    return <div className={classNames("content-locked", { 'content-locked-card': !inDrawer, 'content-locked-banner': isBannerFormat })} {...props} ref={ref}>
        {
            selectedContent.onlyIcon
                ? <div className="content-locked-head-only-icon">{selectedContent.onlyIcon}</div>
                : <div className="content-locked-head" style={backgroundStyle}>
                    <img src={selectedContent.image} alt="" height={selectedContent.imageHeight || 'auto'} className="content-locked-image" />
                </div>
        }
        <div className="content-locked-body">
            <div className="content-locked-title">{selectedContent.title}</div>
            {selectedContent.description && <div className="content-locked-description">{selectedContent.description}</div>}
            {selectedContent.btnText && (
                <div className="content-locked-action">
                    <Button variant={BUTTON_VARIANTS.REVERSE} big full onClick={() => handleAction(selectedContent.action)}>{selectedContent.btnText}</Button>
                    {inDrawer && <Button variant={BUTTON_VARIANTS.CTA_LIGHT} big full onClick={handleCancel}>{BUTTON_TEXTS.CANCEL}</Button>}
                </div>
            )}
        </div>
    </div>
}

export default ContentLocked;
